import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Bell, Menu, X, AlertTriangle, MessageSquare, Search, ChevronLeft, ChevronDown, Trophy, BarChart3, LifeBuoy, GraduationCap, User as UserIcon, Headphones } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { Logo } from "@/components/Logo";
import RoleBadge from "@/components/RoleBadge";
import api from "@/lib/api";
import { getNavSections } from "@/pages/navConfigs";

const MANAGER_ROLES = ["admin", "regional_pm", "social_media_manager", "head_media_comm"];

export default function Layout({ children }) {
  const { user, logout, customRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("munners_nav_collapsed") === "1");
  const [chatUnread, setChatUnread] = useState(0);
  const [notif, setNotif] = useState({ loa: { items: [], count: 0 }, app: { items: [], unread: 0 } });

  const sections = user ? getNavSections(user.role, customRole) : [];
  const showReports = user?.role === "admin";
  const isPrincipal = user?.role === "school_principal";

  useEffect(() => { localStorage.setItem("munners_nav_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    if (!user) return;
    // Active-time tracker: one heartbeat per minute while the tab is visible
    // and the member interacted in the last 5 minutes (idle tabs don't count).
    let lastInput = Date.now();
    const mark = () => { lastInput = Date.now(); };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const localDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && Date.now() - lastInput < 5 * 60000) {
        api.post("/activity/heartbeat", { date: localDay() }).catch(() => {});
      }
    }, 60000);
    return () => { clearInterval(id); evts.forEach((e) => window.removeEventListener(e, mark)); };
  }, [user]);
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try { const r = await api.get("/dm/unread-count"); setChatUnread(r.data?.count || 0); } catch { /* offline */ }
      const next = {};
      if (MANAGER_ROLES.includes(user.role)) { try { next.loa = (await api.get("/loas/notifications/me")).data; } catch { /* none */ } }
      try { next.app = (await api.get("/notifications/me")).data; } catch { /* none */ }
      setNotif((n) => ({ ...n, ...next }));
    };
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogout = async () => { await logout(); nav("/login"); };
  const go = (to) => { setOpen(false); nav(to); };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 flex items-center gap-3 px-3 lg:px-5 bg-white border-b border-slate-200 sticky top-0 z-40" data-testid="app-header">
        <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="menu-toggle"><Menu className="w-5 h-5" /></button>
        <button onClick={() => go("/dashboard")} className="shrink-0" data-testid="header-logo"><Logo size="sm" /></button>
        <HeaderSearch sections={sections} go={go} />
        <div className="flex items-center gap-1 ml-auto">
          {(
            <button onClick={() => go("/dashboard/helpdesk")} className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-sky-600 hover:bg-sky-50" data-testid="header-helpdesk-btn">
              <Headphones className="w-4 h-4" /> Help desk
            </button>
          )}
          <HelpMenu go={go} />
          <span className="hidden md:block w-px h-7 bg-slate-200 mx-1" />
          {!isPrincipal && <IconBtn title="Leaderboard" onClick={() => go("/dashboard/leaderboard")} testid="header-leaderboard-btn"><Trophy className="w-5 h-5" /></IconBtn>}
          <IconBtn title="Chat" onClick={() => go("/dashboard/chat")} testid="chat-bell-btn" badge={chatUnread} badgeColor="bg-indigo-500"><MessageSquare className="w-5 h-5" /></IconBtn>
          {showReports && <IconBtn title="Reports" onClick={() => go("/dashboard/reports")} testid="header-reports-btn"><BarChart3 className="w-5 h-5" /></IconBtn>}
          <NotificationBell notif={notif} setNotif={setNotif} go={go} />
          <span className="hidden md:block w-px h-7 bg-slate-200 mx-1" />
          <UserMenu user={user} customRole={customRole} go={go} onLogout={handleLogout} />
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}
        <aside className={`${open ? "fixed inset-y-0 left-0 z-50 w-72 flex" : "hidden"} lg:flex lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] ${collapsed ? "lg:w-[72px]" : "lg:w-64"} flex-col bg-white border-r border-slate-200 transition-[width] duration-200`} data-testid="app-sidebar">
          <div className="flex items-center justify-between lg:justify-end p-2">
            <span className="lg:hidden pl-2"><Logo size="sm" /></span>
            <button className="lg:hidden p-2" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            <button onClick={() => setCollapsed((c) => !c)} className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600" data-testid="sidebar-collapse-btn" title={collapsed ? "Expand" : "Collapse"}>
              <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
            </button>
          </div>
          <nav className="px-2.5 pb-4 flex-1 overflow-y-auto custom-scrollbar">
            {sections.map((sec, i) => (
              <div key={sec.key} className={i > 0 ? "mt-2 pt-3 border-t border-slate-100" : ""} data-testid={`nav-section-${sec.key}`}>
                {sec.title && !collapsed && <div className="px-3 pb-1.5 text-[13px] font-bold text-slate-900">{sec.title}</div>}
                {sec.items.map((item) => <NavItem key={item.to} item={item} collapsed={collapsed} onClick={() => setOpen(false)} />)}
              </div>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

function NavItem({ item, collapsed, onClick }) {
  return (
    <NavLink to={item.to} end={item.end} onClick={onClick} data-testid={`nav-${item.testid}`} title={collapsed ? item.label : undefined}
      className={({ isActive }) => `flex items-center gap-3 px-2.5 py-2 rounded-xl mb-0.5 text-[15px] transition-colors ${collapsed ? "lg:justify-center" : ""} ${isActive ? "bg-sky-50 text-slate-900 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
      <span className={`w-7 h-7 rounded-lg ${item.color} flex items-center justify-center shrink-0 shadow-sm`}><item.Icon className="w-4 h-4 text-white" strokeWidth={2.2} /></span>
      <span className={`truncate ${collapsed ? "lg:hidden" : ""}`}>{item.label}</span>
    </NavLink>
  );
}

function IconBtn({ title, onClick, testid, badge = 0, badgeColor = "bg-rose-500", children }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid} className="relative p-2 rounded-full text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
      {children}
      {badge > 0 && <span className={`absolute top-0.5 right-0.5 min-w-[16px] h-4 rounded-full ${badgeColor} text-white text-[9px] font-bold flex items-center justify-center px-1`} data-testid={`${testid}-badge`}>{badge > 99 ? "99+" : badge}</span>}
    </button>
  );
}

function useOutside(ref, close) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, close]);
}

function HeaderSearch({ sections, go }) {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setFocus(false));
  const all = useMemo(() => [...sections.flatMap((s) => s.items), { to: "/dashboard/profile", label: "My Profile", Icon: UserIcon, color: "bg-slate-500" }, { to: "/dashboard/chat", label: "Chat", Icon: MessageSquare, color: "bg-emerald-500" }, { to: "/dashboard/helpdesk", label: "Help desk", Icon: LifeBuoy, color: "bg-sky-500" }], [sections]);
  const hits = q.trim() ? all.filter((i) => i.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8) : [];
  const pick = (to) => { setQ(""); setFocus(false); go(to); };
  return (
    <div ref={ref} className="relative hidden md:block w-full max-w-md ml-4">
      <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)} onKeyDown={(e) => e.key === "Enter" && hits[0] && pick(hits[0].to)}
        placeholder="Search anything" className="w-full h-10 pl-10 pr-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300" data-testid="header-search" />
      {focus && hits.length > 0 && (
        <div className="absolute top-12 left-0 right-0 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50" data-testid="header-search-results">
          {hits.map((h) => (
            <button key={h.to} onClick={() => pick(h.to)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-sm text-left" data-testid={`search-hit-${h.to.split("/").pop() || "home"}`}>
              <span className={`w-6 h-6 rounded-md ${h.color} flex items-center justify-center`}><h.Icon className="w-3.5 h-3.5 text-white" /></span>{h.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HelpMenu({ go }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  return (
    <div ref={ref} className="relative hidden md:block">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold text-sky-600 hover:bg-sky-50" data-testid="header-help-menu">Help <ChevronDown className="w-4 h-4" /></button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-50">
          <button onClick={() => { setOpen(false); go("/dashboard/learn"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50" data-testid="help-menu-learn"><GraduationCap className="w-4 h-4 text-pink-500" /> Learn Dashboard</button>
          <button onClick={() => { setOpen(false); go("/dashboard/helpdesk"); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50" data-testid="help-menu-helpdesk"><LifeBuoy className="w-4 h-4 text-sky-500" /> Help desk & tickets</button>
        </div>
      )}
    </div>
  );
}

function NotificationBell({ notif, setNotif, go }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const loa = notif.loa || { items: [], count: 0 };
  const app = notif.app || { items: [], unread: 0 };
  const total = (loa.count || 0) + (app.unread || 0);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && app.unread) {
      try { await api.post("/notifications/read-all"); setNotif((n) => ({ ...n, app: { ...app, unread: 0 } })); } catch { /* ignore */ }
    }
  };
  return (
    <div ref={ref} className="relative">
      <IconBtn title="Notifications" onClick={toggle} testid="loa-bell-btn" badge={total}><Bell className="w-5 h-5" /></IconBtn>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50" data-testid="loa-bell-dropdown">
          <div className="p-3.5 border-b border-slate-100 font-display font-bold text-sm">Notifications</div>
          <div className="max-h-96 overflow-y-auto">
            {app.items.length === 0 && loa.items.length === 0 && <div className="p-6 text-center text-xs text-slate-400">You're all caught up.</div>}
            {loa.items.map((n) => (
              <button key={n.loa_id} onClick={() => { setOpen(false); go("/dashboard/team"); }} className="w-full p-3 text-left hover:bg-slate-50 border-b border-slate-50" data-testid={`bell-item-${n.loa_id}`}>
                <div className="text-sm font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> LOA · {n.issued_to_name}</div>
                <div className="text-[11px] text-slate-500">{n.expired ? <span className="text-rose-600 font-bold">EXPIRED ({Math.abs(n.days_left)}d ago)</span> : <span className="text-amber-600 font-bold">Expires in {n.days_left}d</span>}</div>
              </button>
            ))}
            {app.items.map((n) => (
              <button key={n.notification_id} onClick={() => { setOpen(false); if (n.link) go(n.link); }} className={`w-full p-3 text-left hover:bg-slate-50 border-b border-slate-50 ${n.read ? "" : "bg-sky-50/50"}`} data-testid={`notif-item-${n.notification_id}`}>
                <div className="text-sm font-semibold">{n.title}</div>
                {n.body && <div className="text-[11px] text-slate-500 line-clamp-2">{n.body}</div>}
                <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu({ user, customRole, go, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100" data-testid="user-avatar">
        <span className="w-9 h-9 rounded-full bg-emerald-500 text-white font-bold text-sm flex items-center justify-center overflow-hidden">
          {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : initials}
        </span>
        <span className="hidden lg:block text-sm font-semibold text-sky-700 max-w-[160px] truncate">{user?.name}</span>
        <ChevronDown className="hidden lg:block w-4 h-4 text-sky-700" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50" data-testid="user-menu">
          <div className="p-4 border-b border-slate-100">
            <div className="font-semibold text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate mb-1.5">{user?.email}</div>
            <RoleBadge role={user?.role} customRoles={customRole ? [customRole] : []} size="xs" showCustomTag={false} />
          </div>
          <button onClick={() => { setOpen(false); go("/dashboard/profile"); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50" data-testid="sidebar-profile-link"><UserIcon className="w-4 h-4" /> My Profile</button>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, icon: Icon, actions, accent = "orange" }) {
  const accentBg = {
    orange: "from-orange-100 to-amber-50 text-orange-600",
    pink: "from-pink-100 to-rose-50 text-pink-600",
    teal: "from-teal-100 to-cyan-50 text-teal-600",
    indigo: "from-indigo-100 to-blue-50 text-indigo-600",
    violet: "from-violet-100 to-fuchsia-50 text-violet-600",
    yellow: "from-yellow-100 to-amber-50 text-amber-600",
  }[accent] || "from-orange-100 to-amber-50 text-orange-600";
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div className="flex items-center gap-4">
        {Icon && (
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${accentBg} flex items-center justify-center`}>
            <Icon className="w-7 h-7" />
          </div>
        )}
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight">{title}</h1>
          {subtitle && <p className="text-slate-500 text-sm mt-1">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, trend, color = "orange" }) {
  const colors = {
    orange: "from-orange-500 to-amber-400",
    teal: "from-teal-500 to-cyan-400",
    indigo: "from-indigo-500 to-blue-400",
    pink: "from-pink-500 to-rose-400",
    violet: "from-violet-500 to-fuchsia-400",
    green: "from-green-500 to-emerald-400",
  };
  return (
    <div className="dashboard-card p-5 relative overflow-hidden">
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${colors[color]} opacity-10`} />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-3xl font-display font-black text-slate-900 mt-2">{value}</div>
          {trend && <div className="text-xs text-slate-500 mt-1">{trend}</div>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center text-white`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}
